import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroCheckbox,
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import {
	BaseLender,
	BaseRateTypes,
	BaseRateTypesMap,
	Deal,
	FacilityFactorTypes,
	FacilityFactorTypesMap,
	FacilityTypeMap,
	FacilityTypes,
	InterestRateTypes,
	InterestRateTypesMap,
	PartialBank,
	PersonalGuaranteeTypes,
	PersonalGuaranteeTypesMap,
	RateTypes,
	RateTypesMap,
	TermSheetTypes,
	Tier1BankTypeMap,
	Tier1BankTypes,
} from '../../shared/models';
import { AxiosUtils, FormikUtils, NumberUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { LendersAutoComplete } from '../Lenders/LendersAutoComplete';
import { MaxCurrencyLength } from '../../shared/utils/platform';
import { isEither, isLeaseFacilityType } from '../../shared/utils/entity';
import { EmptyGuid } from '../../shared/constants';
import { validateDateWithinPlusMinus100Years } from '../../shared/utils/date';

interface AddTermSheetFormProps {
	dealGuid: string | undefined;
	deal: Deal;
	isAdding: boolean;
	close: () => void;
}

export const AddTermSheetForm: React.FC<AddTermSheetFormProps> = ({ dealGuid, deal, isAdding, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onGetBankRateValue = async (
		value: BaseRateTypes | undefined,
		setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
	) => {
		const rateValue = await AxiosUtils.getResponseData<number | undefined>(
			api.get(CerebroApiEndpoints.TermSheets.HistoricalBankRate(EmptyGuid, value))
		);

		setFieldValue('baseRateValue', NumberUtils.FormatFloat(rateValue, 2));
	};

	const calculateMonthlyPayment = (values: AddTermSheetFormValues): number => {
		const highEndAmount = NumberUtils.ParseDecimal(values.highEndAmount);
		const leaseRateFactor =
			values.facilityFactorType == 'PaymentAmount' ? undefined : NumberUtils.ParseDecimal(values.leaseRateFactor);

		return (leaseRateFactor ?? 0.0) * (highEndAmount ?? 0.0);
	};

	const calculateInterestRate = (values: AddTermSheetFormValues): number => {
		const highEndAmount = NumberUtils.ParseDecimal(values.highEndAmount) ?? 0.0;
		const highEndMaturity = NumberUtils.ParseDecimal(values.highEndMaturity) ?? 0.0;
		const leaseRateFactor =
			values.facilityFactorType == 'PaymentAmount'
				? 0.0
				: NumberUtils.ParseDecimal(values.leaseRateFactor) ?? 0.0;
		const leaseResidual = NumberUtils.ParseDecimal(values.leaseResidual) ?? 0.0;
		const maturityDenominator = highEndMaturity;
		const denominator = maturityDenominator != 0 ? (highEndAmount - leaseResidual) / 2 / maturityDenominator : 0.0;
		const interestRate =
			denominator != 0 ? (leaseRateFactor * highEndAmount * (highEndMaturity * 12)) / denominator : 0.0;

		return interestRate;
	};

	const addSheetMutation = useMutation(
		async ({ values }: AddTermSheetFormMutationVariables) => {
			const createDto = {
				addToDebtSchedule: values.addToDebtSchedule,
				dateReceived: values.dateReceived,
				facilityType: values.facilityType,
				hasPersonalGuaranty: values.hasPersonalGuaranty,
				highEndAmount: NumberUtils.ParseDecimal(values.highEndAmount),
				highEndMaturity: NumberUtils.ParseDecimal(values.highEndMaturity),
				interestCash: NumberUtils.ParseDecimal(values.interestCash),
				interestRateType: values.interestRateType,
				isIncumbentSolution: values.isIncumbentSolution,
				lender: values.lender,
				maturityDate: values.maturityDate,
				newLender: values.newLender,
				newLenderName: values.newLenderName,
				termsEffectiveDate: values.termsEffectiveDate,
				tier1BankType: values.tier1BankType,
				personalGuaranteeType: values.personalGuaranteeType,
				facilityFactorType: values.facilityFactorType,
				leaseRateFactor: NumberUtils.ParseDecimal(values.leaseRateFactor),
				leaseResidual: NumberUtils.ParseDecimal(values.leaseResidual),
				leasePaymentAmount: NumberUtils.ParseDecimal(values.leasePaymentAmount),
				baseRate: values.baseRate,
				baseRateFloor: NumberUtils.ParseDecimal(values.baseRateFloor),
				baseRateValue: NumberUtils.ParseDecimal(values.baseRateValue),
				rateType: values.rateType,
				refinance: values.refinance,
			};

			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.TermSheets.Add(dealGuid), createDto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Existing debt term sheet was added.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.ExistingTerms(dealGuid));
				client.invalidateQueries(QueryKeys.DebtSchedule(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.Root(dealGuid));
				client.invalidateQueries(QueryKeys.Matching.Get(dealGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={isAdding} onClose={close}>
			<CerebroHeading>Add Existing Debt Term</CerebroHeading>

			<Formik
				initialValues={
					{
						isIncumbentSolution: true,
						dateReceived: new Date(),
						termsEffectiveDate: new Date(),
						termSheetType: 'ExistingDebtTerms' as TermSheetTypes,
						lender: null,
						newLender: false,
						newLenderName: null,
						tier1BankType: null,
						addToDebtSchedule: true,
						hasPersonalGuaranty: false,
						refinance: false,
						rateType: 'Percentage',
					} as AddTermSheetFormValues
				}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(addSheetMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, setFieldValue, values }) => {
					const showRateFactor =
						values.facilityFactorType == 'RateFactor' || values.facilityFactorType == 'NotApplicable';

					const monthlyPayment = calculateMonthlyPayment(values);
					const interestRate = calculateInterestRate(values);

					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel htmlFor="bankGuid" sx={{ marginTop: 3 }}>
										Select a lender:
									</CerebroLabel>
									<Field
										id="lender"
										name="lender"
										lenders={[] as BaseLender[]}
										component={LendersAutoComplete}
										companyGuid={deal?.companyGuid}
										disabled={values.newLender}
										error={FormikUtils.IsInvalid('lender', errors, touched)}
									/>

									<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
										<Grid item>
											{FormikUtils.IsInvalid('lender', errors, touched) && (
												<CerebroText variant="error">{errors.lender}</CerebroText>
											)}
										</Grid>
										<Grid item>
											<CerebroLabel
												htmlFor="newLender"
												sx={{ display: 'flex', alignItems: 'center' }}
											>
												<Field
													id="newLender"
													name="newLender"
													size="small"
													as={CerebroCheckbox}
													sx={{ padding: 0, paddingRight: 1 }}
												/>
												<CerebroText variant="hint" component="span">
													Include a lender not listed
												</CerebroText>
											</CerebroLabel>
										</Grid>
									</Grid>
								</Grid>

								{values.newLender && (
									<>
										<Grid item xs={11}>
											<CerebroLabel htmlFor="newLenderName">New Lender:</CerebroLabel>
											<Field
												id="newLenderName"
												name="newLenderName"
												as={CerebroInput}
												placeholder="The full name of the lender to exclude"
												error={FormikUtils.IsInvalid('newLenderName', errors, touched)}
											/>
											{FormikUtils.IsInvalid('newLenderName', errors, touched) && (
												<CerebroText variant="error">{errors.newLenderName}</CerebroText>
											)}
										</Grid>

										<Grid item xs={11}>
											<CerebroLabel id="tier1BankType">Tier 1 Bank Type</CerebroLabel>
											<Field
												id="tier1BankType"
												as={CerebroDropdown}
												name="tier1BankType"
												disabled={isSubmitting}
											>
												{Object.entries(Tier1BankTypeMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>
											{FormikUtils.IsInvalid('tier1BankType', errors, touched) && (
												<CerebroText variant="error">{errors.tier1BankType}</CerebroText>
											)}
										</Grid>
									</>
								)}
								<Grid item xs={11}>
									<CerebroLabel id="facilityType" required>
										What best describes the loan type?
									</CerebroLabel>
									<Field
										id="facilityType"
										as={CerebroDropdown}
										name="facilityType"
										disabled={isSubmitting}
									>
										{Object.entries(FacilityTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="highEndAmount" required>
										What is the commitment amount?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="highEndAmount"
										name="highEndAmount"
										disabled={isSubmitting}
										error={touched.highEndAmount && errors.highEndAmount !== undefined}
									/>
									{errors.highEndAmount && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.highEndAmount}
										</CerebroText>
									)}
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="highEndMaturity" required>
										What is the term/maturity (in years)?
									</CerebroLabel>
									<Field
										as={CerebroMaskedInput}
										allowDecimals={true}
										minDecimalPlaces={1}
										maxDecimalPlaces={2}
										id="highEndMaturity"
										name="highEndMaturity"
										disabled={isSubmitting}
										error={touched.highEndMaturity && errors.highEndMaturity !== undefined}
									/>
									{errors.highEndMaturity && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.highEndMaturity}
										</CerebroText>
									)}
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="maturityDate" required>
										Maturity Date
									</CerebroLabel>
									<Field
										id="maturityDate"
										name="maturityDate"
										component={CerebroDatePicker}
										disabled={isSubmitting}
										error={touched.maturityDate && errors.maturityDate !== undefined}
									/>
									{errors.maturityDate && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.maturityDate}
										</CerebroText>
									)}
								</Grid>

								{isLeaseFacilityType(values.facilityType) && (
									<>
										<Grid item xs={11}>
											<CerebroLabel id="facilityFactorType">
												Rate Factor/Payment Amount?
											</CerebroLabel>
											<Field
												id="facilityFactorType"
												as={CerebroDropdown}
												name="facilityFactorType"
												disabled={isSubmitting}
											>
												{Object.entries(FacilityFactorTypesMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>
										</Grid>
										{showRateFactor && (
											<Grid item xs={11}>
												<CerebroLabel id="leaseRateFactor">
													What is the lease rate factor for the facility?
												</CerebroLabel>
												<Field
													as={CerebroMaskedInput}
													allowDecimals={true}
													minDecimalPlaces={2}
													maxDecimalPlaces={7}
													id="leaseRateFactor"
													name="leaseRateFactor"
													placeholder={'Enter as decimal value, e.g., 0.02355 and not 2.355%'}
													disabled={isSubmitting}
													error={
														touched.leaseRateFactor && errors.leaseRateFactor !== undefined
													}
												/>
												<CerebroText>
													Monthly Payment: {NumberUtils.FormatCurrency(monthlyPayment)}
												</CerebroText>
												<CerebroText>
													Effective Interest Rate: {interestRate.toFixed(2)}%
												</CerebroText>
												{errors.leaseRateFactor && (
													<CerebroText variant="error" sx={{ marginBottom: 4 }}>
														{errors.leaseRateFactor}
													</CerebroText>
												)}
											</Grid>
										)}
										{values.facilityFactorType == 'PaymentAmount' && (
											<Grid item xs={11}>
												<CerebroLabel id="leasePaymentAmount">
													What is the lease payment amount?
												</CerebroLabel>
												<Field
													as={CerebroMaskedInput}
													id="leasePaymentAmount"
													name="leasePaymentAmount"
													disabled={isSubmitting}
													error={
														touched.leasePaymentAmount &&
														errors.leasePaymentAmount !== undefined
													}
												/>
												{errors.leasePaymentAmount && (
													<CerebroText variant="error" sx={{ marginBottom: 4 }}>
														{errors.leasePaymentAmount}
													</CerebroText>
												)}
											</Grid>
										)}
										<Grid item xs={11}>
											<CerebroLabel id="leaseResidual">
												What is the residual value for lease buyout?
											</CerebroLabel>
											<Field
												as={CerebroMaskedInput}
												id="leaseResidual"
												name="leaseResidual"
												disabled={isSubmitting}
												error={touched.leaseResidual && errors.leaseResidual !== undefined}
											/>
											{errors.leaseResidual && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.leaseResidual}
												</CerebroText>
											)}
										</Grid>
										<Grid item xs={11}>
											<CerebroLabel id="exerciseLeaseResidual">
												Will Borrower exercise residual?
											</CerebroLabel>
											<Field
												as={CerebroYesNo}
												id="exerciseLeaseResidual"
												name="exerciseLeaseResidual"
												disabled={isSubmitting}
											/>
										</Grid>
									</>
								)}

								{isEither(values.facilityType) && (
									<>
										<Grid item xs={11}>
											<CerebroLabel required>Interest Rate Type</CerebroLabel>
											<Field
												id="interestRateType"
												as={CerebroDropdown}
												name="interestRateType"
												disabled={isSubmitting}
												error={
													touched.interestRateType && errors.interestRateType !== undefined
												}
											>
												{Object.entries(InterestRateTypesMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>
											{errors.interestRateType && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.interestRateType}
												</CerebroText>
											)}
										</Grid>
										<Grid item xs={7}>
											<CerebroLabel id="interestCash" required>
												Annual Cash Interest
											</CerebroLabel>
											<Field
												as={CerebroMaskedInput}
												allowDecimals={true}
												minDecimalPlaces={1}
												maxDecimalPlaces={2}
												id="interestCash"
												name="interestCash"
												disabled={isSubmitting}
												error={touched.interestCash && errors.interestCash !== undefined}
											/>
											{errors.interestCash && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.interestCash}
												</CerebroText>
											)}
										</Grid>
										<Grid item xs={4}>
											<CerebroLabel id="rateType">&nbsp;</CerebroLabel>
											<Field
												id="rateType"
												as={CerebroDropdown}
												name="rateType"
												disabled={isSubmitting}
											>
												{Object.entries(RateTypesMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>
										</Grid>
									</>
								)}

								{values.interestRateType == 'Float' && !isLeaseFacilityType(values.facilityType) && (
									<>
										<Grid item xs={7}>
											<CerebroLabel id="baseRate" required>
												Base Type
											</CerebroLabel>
											<Field
												id="baseRate"
												as={CerebroDropdown}
												name="baseRate"
												disabled={isSubmitting}
												error={touched.baseRate && errors.baseRate !== undefined}
												onChange={(e: any) => {
													setFieldValue('baseRate', e.target.value);
													onGetBankRateValue(e.target.value as BaseRateTypes, setFieldValue);
												}}
											>
												{Object.entries(BaseRateTypesMap).map(([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												))}
											</Field>
											{errors.baseRate && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.baseRate}
												</CerebroText>
											)}
										</Grid>
										<Grid item xs={4}>
											<CerebroText>&nbsp;</CerebroText>
											{values.baseRate != 'Other' && (
												<CerebroText sx={{ marginTop: 3 }}>{values.baseRateValue}%</CerebroText>
											)}
										</Grid>
										{values.baseRate == 'Other' && (
											<>
												<Grid item xs={7}>
													<CerebroLabel id="otherBaseRateName">
														Name of reference base rate
													</CerebroLabel>
													<Field
														as={CerebroInput}
														id="otherBaseRateName"
														name="otherBaseRateName"
														disabled={isSubmitting}
													/>
												</Grid>
												<Grid item xs={5}>
													<CerebroLabel id="otherBaseRateValue">Current Value</CerebroLabel>
													<Field
														as={CerebroMaskedInput}
														allowDecimals={true}
														minDecimalPlaces={1}
														maxDecimalPlaces={4}
														id="otherBaseRateValue"
														name="otherBaseRateValue"
														placeholder="enter as a percent (i.e. 3.75 for 3.75%)"
														disabled={isSubmitting}
													/>
												</Grid>
											</>
										)}
										<Grid item xs={11}>
											<CerebroLabel id="baseRateFloor">Base Rate Floor</CerebroLabel>
											<Field
												as={CerebroMaskedInput}
												allowDecimals={true}
												minDecimalPlaces={1}
												maxDecimalPlaces={2}
												id="baseRateFloor"
												name="baseRateFloor"
												disabled={isSubmitting}
											/>
										</Grid>
									</>
								)}

								<Grid item xs={11}>
									<CerebroLabel id="hasPersonalGuaranty">Has Guaranty?</CerebroLabel>
									<Field
										id="hasPersonalGuaranty"
										as={CerebroYesNo}
										name="hasPersonalGuaranty"
										disabled={isSubmitting}
									/>
								</Grid>

								{values.hasPersonalGuaranty && (
									<Grid item xs={11}>
										<CerebroLabel id="personalGuaranteeType">Guarantee Entity</CerebroLabel>
										<Field
											id="personalGuaranteeType"
											as={CerebroDropdown}
											name="personalGuaranteeType"
											disabled={isSubmitting}
											error={
												touched.personalGuaranteeType &&
												errors.personalGuaranteeType !== undefined
											}
										>
											{Object.entries(PersonalGuaranteeTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{errors.personalGuaranteeType && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.personalGuaranteeType}
											</CerebroText>
										)}
									</Grid>
								)}

								<Grid item xs={11}>
									<CerebroLabel id="addToDebtSchedule">Add to Debt Schedule</CerebroLabel>
									<Field
										id="addToDebtSchedule"
										as={CerebroYesNo}
										name="addToDebtSchedule"
										disabled={isSubmitting}
									/>
								</Grid>
								{values.addToDebtSchedule && (
									<Grid item xs={11}>
										<CerebroLabel id="refinance">Refinance</CerebroLabel>
										<Field
											id="refinance"
											as={CerebroYesNo}
											name="refinance"
											disabled={isSubmitting}
										/>
									</Grid>
								)}
								<Grid item xs={11}>
									<CerebroLabel id="isIncumbentSolution">Incumbent Solution</CerebroLabel>
									<Field
										id="isIncumbentSolution"
										as={CerebroYesNo}
										name="isIncumbentSolution"
										disabled={isSubmitting}
									/>
								</Grid>
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} disabled={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddTermSheetFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddTermSheetFormValues>;
interface AddTermSheetFormValues {
	isIncumbentSolution: boolean;
	dateReceived: Date;
	termsEffectiveDate: Date;
	termSheetType: TermSheetTypes;
	lender: PartialBank | null;
	newLender: boolean;
	newLenderName: string | null;
	tier1BankType: Tier1BankTypes | null;
	hasPersonalGuaranty: boolean;
	personalGuaranteeType: PersonalGuaranteeTypes | undefined;
	facilityType: FacilityTypes | undefined;
	maturityDate: Date | undefined;
	highEndAmount: string;
	highEndMaturity: string;
	facilityFactorType: FacilityFactorTypes;
	leaseRateFactor: string;
	leaseResidual: string;
	leasePaymentAmount: string;
	interestRateType: InterestRateTypes | undefined;
	baseRate: BaseRateTypes | undefined;
	baseRateFloor: string;
	baseRateValue: string;
	interestCash: string;
	rateType: RateTypes | undefined;
	refinance: boolean;
	addToDebtSchedule: boolean;
}

type AddTermSheetFormValueErrors = FormikErrors<AddTermSheetFormValues>;

function validateForm(values: AddTermSheetFormValues) {
	let errors: AddTermSheetFormValueErrors = {};

	const isLease = isLeaseFacilityType(values.facilityType);
	const showRateFactor = values.facilityFactorType == 'RateFactor' || values.facilityFactorType == 'NotApplicable';

	errors = processLeaseBasedValuesErrors(values, errors, isLease, showRateFactor);
	errors = processNonLeaseBasedValuesErrors(values, errors, isLease);

	if (!values.highEndAmount || values.highEndAmount === '') {
		errors.highEndAmount = 'Enter a valid commitment amount.';
	}

	errors = processTermValuesErrors(values, errors);
	errors = processNewLenderValuesErrors(values, errors);

	const maturityDateError = validateDateWithinPlusMinus100Years(values.maturityDate ?? null, 'Maturity Date');
	if (maturityDateError) {
		errors.maturityDate = maturityDateError;
	}

	const dateReceivedError = validateDateWithinPlusMinus100Years(values.dateReceived ?? null, 'Date Received');
	if (dateReceivedError) {
		errors.dateReceived = dateReceivedError;
	}
	
	if (!values.termsEffectiveDate) {
		errors.termsEffectiveDate = 'You must select a valid date the term sheet was effective.';
	}

	return errors;
}

function processTermValuesErrors(
	values: AddTermSheetFormValues,
	errors: AddTermSheetFormValueErrors
): AddTermSheetFormValueErrors {
	const termCheck = NumberUtils.ParseDecimal(values.highEndMaturity);
	if (!values.highEndMaturity || values.highEndMaturity === '' || !termCheck) {
		errors.highEndMaturity = 'Enter a valid term/maturity.';
	} else if (termCheck > 99) {
		errors.highEndMaturity = 'Term/maturity cannot exceed 99 years.';
	}

	return errors;
}

function processNonLeaseBasedValuesErrors(
	values: AddTermSheetFormValues,
	errors: AddTermSheetFormValueErrors,
	isLease: boolean
): AddTermSheetFormValueErrors {
	if (isLease) {
		return errors;
	}

	if (!values.interestRateType) {
		errors.interestRateType = 'Interest rate type is required.';
	}

	if (!values.interestCash) {
		errors.interestCash = 'Annual cash interest is required.';
	}

	return errors;
}

function processLeaseBasedValuesErrors(
	values: AddTermSheetFormValues,
	errors: AddTermSheetFormValueErrors,
	isLease: boolean,
	showRateFactor: boolean
): AddTermSheetFormValueErrors {
	if (!isLease) {
		return errors;
	}

	if (showRateFactor && !values.leaseRateFactor) {
		errors.leaseRateFactor = 'Lease factor rate is required.';
	}
	if (values.facilityFactorType == 'PaymentAmount' && !values.leasePaymentAmount) {
		errors.leasePaymentAmount = 'Lease payment amount is required.';
	}
	if (!values.leaseResidual) {
		errors.leaseResidual = 'Lease residual amount is required.';
	}

	return errors;
}

function processNewLenderValuesErrors(
	values: AddTermSheetFormValues,
	errors: AddTermSheetFormValueErrors
): AddTermSheetFormValueErrors {
	if (values.newLender) {
		if (!values.newLenderName) {
			errors.newLenderName = 'You must enter a valid bank name.';
		}

		if (!values.tier1BankType) {
			errors.tier1BankType = 'You must select a tier 1 bank type to add a lender.';
		}
	} else {
		if (!values.lender || !values.lender.name) {
			errors.lender = 'You must select a lender associated to the terms.';
		}
	}

	return errors;
}
